import React, { useState, Fragment } from "react"
import { useSelector } from "react-redux"
import i18n from "consts/i18n"
import { get } from "lodash"
import PropTypes from "prop-types"

import { selectReferencesModel } from "selectors/references"

import ProgramDisplay from "containers/ProgramDisplay/ProgramDisplay"
import PageActions from "components/PageActions/PageActions"
import BroadcastList from "components/BroadcastList/BroadcastList"
import GridTitle from "components/GridTitle/GridTitle"
import VideoGrid from "components/VideoGrid/VideoGrid"
import TilePerson from "components/TilePerson/TilePerson"
import TileVideo from "components/TileVideo/TileVideo"

import { useOnCTAClick } from "helpers/CTA"
import { programsWithSeoText } from "./helpers"
import { getBottomtext, getSeoText } from "./texts"

import styles from "./pageProgram.css"
import BreadCrumbWrapper from "components/BreadCrumb/BreadCrumbWrapper"
import consts from "consts/index"
import FormatterHelpers from "helpers/formatter"
import { LOCALE } from "consts/locale"

const PageProgramView = ({ program }) => {
  const references = useSelector(selectReferencesModel)
  const seo = useSelector(state => state.seo)

  const onCTAClick = useOnCTAClick({ program })
  const [isSticky, setIsSticky] = useState(false)
  const channel = program.getChannel()

  const isEpisode = program.isEpisode()
  const category = isEpisode ? false : references.getCategory(program.getCategoryId())
  const subCategory = isEpisode ? false : references.getSubCategory(program.getSubCategoryId())
  const isCharged = !channel.isAvailableInFreeBundles(get(seo, "settings.tv_bundles_visible", []))

  const bottomText = getBottomtext(program.getId(), channel.getUrl())
  const seoText = getSeoText(program.getId())

  const episodeSection = program.getEpisodeSections().filter(section => section.getItemsCount())
  const channelEspisodeSection = program.getChannelEpisodeSections().filter(section => section.getItemsCount())
  const casting = program.getCasting()
  const shouldRenderProgramDetails = Boolean(episodeSection.length > 0 || channelEspisodeSection.length > 0 || casting)
  const programIsUnavailable = !program?.pending && (!program?.data?.is_available || program?.getBroadcastDate() === i18n.not_broadcasted)

  const onClick = useOnCTAClick({ program })

  const seoTextNeeded = programsWithSeoText.includes(program.getId())

  const breadcrumb = [
    {
      route: consts.routes.default,
      name: consts.i18n.breadcrumb.firstItem,
    },
    {
      route: FormatterHelpers.basic(consts.routes.seoHome, { locale: LOCALE }),
      name: consts.i18n.breadcrumb.programmeTV,
    },
  ]

  if (category) {
    breadcrumb.push({
      route: FormatterHelpers.basic(consts.routes.category, { locale: LOCALE, categoryId: category.id, categorySlug: category.slug_seo }),
      name: category.label,
    })
  }

  if (subCategory) {
    breadcrumb.push({
      route: FormatterHelpers.basic(consts.routes.genre, {
        locale: LOCALE,
        categoryId: category.id,
        categorySlug: category.slug_seo,
        genreId: subCategory.id,
        genreSlug: subCategory.slug_seo,
      }),
      name: subCategory.label,
    })
  }

  if (program.isAvailable()) {
    breadcrumb.push({
      route: program.getChannel().getUrl(),
      name: `${channel.getTitle()} en direct`,
    })
  }

  breadcrumb.push({
    route: program.getUrl(),
    name: program.data.title,
  })

  const view = (
    <BreadCrumbWrapper steps={breadcrumb}>
      <div className={styles.section}>
        <ProgramDisplay
          onScrollBeyondCTA={setIsSticky}
          program={program}
          channel={channel}
          category={category}
          subCategory={subCategory}
          isCharged={isCharged}
        />
        {shouldRenderProgramDetails && (
          <div className={styles.programDetails}>
            {episodeSection.map(section => (
              <div className={styles.broadcasts} key={section.getSlug()}>
                <h2>{section.getTitle()}</h2>
                <BroadcastList
                  broadcasts={section.getItems()}
                  type={section.getSlug() === "episodes-futures" ? "record" : "play"}
                  onClick={onClick}
                />
              </div>
            ))}
            {channelEspisodeSection.length > 0 && (
              <div>
                {channelEspisodeSection.map(section => (
                  <div key={section.getSlug()}>
                    <GridTitle>{section.getTitle()}</GridTitle>
                    <VideoGrid>
                      {section.getItems().map((program, index) => (
                        <TileVideo
                          key={`${index}-${program.getId()}`}
                          isLive={section.isLive()}
                          useMicroData={false}
                          displaySubtitle={section.shouldDisplaySubtitle()}
                          tile={program}
                        />
                      ))}
                    </VideoGrid>
                  </div>
                ))}
              </div>
            )}
            {casting ? (
              <div className={styles.castingWrapper}>
                <GridTitle>Au casting dans {program.getTitle()}</GridTitle>
                <ul className={styles.personGrid}>
                  {casting.items.slice(0, 7).map(item => (
                    <li key={item.getId()}>
                      <TilePerson tile={item} />
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>
        )}
        {bottomText && (
          <div className={styles.bottomTextWrapper}>
            <div>{bottomText}</div>
          </div>
        )}
      </div>
    </BreadCrumbWrapper>
  )

  return (
    <>
      {shouldRenderProgramDetails ? (
        <PageActions isShown={isSticky} onClick={onCTAClick} isProgramAvailable={!programIsUnavailable} withGradientBackground={!seoTextNeeded}>
          {view}
        </PageActions>
      ) : (
        view
      )}
      {seoTextNeeded && <div className={styles.seoWrapper}>{seoText}</div>}
    </>
  )
}

PageProgramView.propTypes = {
  program: PropTypes.object,
}

export default PageProgramView
